import { useMutation } from '@tanstack/react-query';
import { produce } from 'immer';
import queryClient from 'react-query-settings/queryClient';

// create mutation can be called with or without an attachable
// domain attachments are kept up-to-date either by setting query data manually or by invalidating the query
// this allows for domain attachments to be kept in sync
export function useCreateAttachmentMutation({
  attachableId,
  attachableType,
  attachmentsQueryKeyUuid,
}) {
  return useMutation({
    mutationKey: ['createAttachment'],
    mutationFn: async ({ fileType, uploadKey }) => {
      const response = await axios.post('/attachments', {
        additional_processing: fileType === 'pdf',
        attachment: {
          attached_key: uploadKey,
          attachable_type: attachableId ? attachableType : null,
          attachable_id: attachableId,
        },
        type: fileType,
      });

      return response.data;
    },
    onSuccess: (responseData) => {
      if (attachableId) return;

      queryClient.setQueryData(
        ['attachments', attachmentsQueryKeyUuid],
        (existingDomainAttachments) =>
          produce(existingDomainAttachments, (draft) => {
            if (!draft) return draft;

            draft.data.push(responseData.data);
            draft.included = [...draft.included, ...responseData.included];
          }),
      );
    },
    onSettled: () => {
      if (!attachableId || !attachableType) return;

      queryClient.invalidateQueries({
        queryKey: ['attachments', attachmentsQueryKeyUuid],
      });
    },
  });
}

// update mutation can be called with or without an attachable
// domain attachments are kept up-to-date either by setting query data manually or by invalidating the query
// this allows for domain attachments to be kept in sync
export function useUpdateAttachmentMutation({
  attachableId,
  attachableType,
  attachmentsQueryKeyUuid,
}) {
  return useMutation({
    mutationKey: ['updateAttachment'],
    mutationFn: async ({ hashid, params, recordType }) => {
      const response = await axios.patch(`/attachments/${hashid}`, {
        type: recordType,
        attachment: params,
      });

      return response.data;
    },
    onSuccess: (data) => {
      if (attachableId) return;

      queryClient.setQueryData(
        ['attachments', attachmentsQueryKeyUuid],
        (existingDomainAttachments) =>
          produce(existingDomainAttachments, (draft) => {
            if (!draft) return draft;

            const index = draft.data.findIndex(
              (attachment) =>
                attachment.attributes.hashid === data.data.attributes.hashid,
            );

            if (index !== -1) draft.data[index] = data.data;
          }),
      );
    },
    onSettled: () => {
      if (!attachableId || !attachableType) return;

      queryClient.invalidateQueries({
        queryKey: ['attachments', attachmentsQueryKeyUuid],
      });
    },
  });
}

// delete mutation is never called without an attachable
export function useDeleteAttachmentMutation({ attachmentsQueryKeyUuid }) {
  return useMutation({
    mutationKey: ['deleteAttachment'],
    mutationFn: async ({ hashid, recordType }) => {
      await axios.delete(`/attachments/${hashid}`, {
        params: { type: recordType },
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['attachments', attachmentsQueryKeyUuid],
      });
    },
  });
}

// create rotate process mutation can be called with or without an attachable
// domain attachments are kept up-to-date either by setting query data manually or by invalidating the query
// this allows for domain attachments to be kept in sync
export function useCreateAttachmentRotationProcessMutation() {
  return useMutation({
    mutationFn: async ({ hashid }) => {
      const response = await axios.post(`/attachments/rotation_processes`, {
        rotation_process: { attachment_id: hashid },
      });

      return response.data;
    },
  });
}
